import React, { useState, useEffect } from 'react';
import { Button } from '../../button';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover';
import { X, Calendar } from 'lucide-react';
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";
import { Input } from '../../input';
import { DatePickerV3 } from "../../date-picker-v3";
import { ensure2230Z } from '../../../lib/date-time';

interface DateRangeFilterProps {
    filterState: {
        filterKey: string;
        filter: any;
        setFilter: (filters: any) => void;
    };
    title: string;
    description?: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    datePickerVersion?: string;
}

const PopoverDateRangeFilterComponent: React.FC<DateRangeFilterProps> = ({
    filterState,
    title,
    description,
    isOpen,
    onOpenChange,
    datePickerVersion = '2'
}) => {
    const currentFilter = filterState.filter[filterState.filterKey] || { from: '', to: '' };
    const [localFromDate, setLocalFromDate] = useState(currentFilter.from || '');
    const [localToDate, setLocalToDate] = useState(currentFilter.to || '');

    // Update local state when filter changes
    useEffect(() => {
        const filter = filterState.filter[filterState.filterKey] || { from: '', to: '' };
        setLocalFromDate(filter.from || '');
        setLocalToDate(filter.to || '');
    }, [filterState.filter, filterState.filterKey]);

    const handleFromDateChangeString = (value: string) => {
        setLocalFromDate(value);
        filterState.setFilter({
            ...filterState.filter,
            [filterState.filterKey]: {
                from: value,
                to: localToDate
            }
        });
    };

    const handleToDateChangeString = (value: string) => {
        setLocalToDate(value);
        filterState.setFilter({
            ...filterState.filter,
            [filterState.filterKey]: {
                from: localFromDate,
                to: value
            }
        });
    };

    const handleFromDateChange = (date: Date | undefined) => {
        if (date) {
            let y = date.getFullYear();
            let m = String(date.getMonth() + 1).padStart(2, "0");
            let d = String(date.getDate()).padStart(2, "0");
            const dateString = `${y}-${m}-${d}`;
            setLocalFromDate(dateString);
            filterState.setFilter({
                ...filterState.filter,
                [filterState.filterKey]: {
                    from: dateString,
                    to: localToDate
                }
            });
        } else {
            setLocalFromDate('');
            filterState.setFilter({
                ...filterState.filter,
                [filterState.filterKey]: {
                    from: '',
                    to: localToDate
                }
            });
        }
    };

    const handleToDateChange = (date: Date | undefined) => {
        if (date) {
            let y = date.getFullYear();
            let m = String(date.getMonth() + 1).padStart(2, "0");
            let d = String(date.getDate()).padStart(2, "0");
            const dateString = `${y}-${m}-${d}`;
            setLocalToDate(dateString);
            filterState.setFilter({
                ...filterState.filter,
                [filterState.filterKey]: {
                    from: localFromDate,
                    to: dateString
                }
            });
        } else {
            setLocalToDate('');
            filterState.setFilter({
                ...filterState.filter,
                [filterState.filterKey]: {
                    from: localFromDate,
                    to: ''
                }
            });
        }
    };

    const handleClear = () => {
        setLocalFromDate('');
        setLocalToDate('');
        filterState.setFilter({
            ...filterState.filter,
            [filterState.filterKey]: {
                from: '',
                to: ''
            }
        });
    };

    const hasActiveFilter = localFromDate || localToDate;

    return (
        <Popover open={isOpen} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <img className="ml-2 w-3 cursor-pointer" src={MagnifyingGlass} alt="Search" />
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="start">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">{title}</h4>
                        {hasActiveFilter && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleClear}
                                className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
                            >
                                <X className="h-3 w-3 mr-1" />
                                Clear
                            </Button>
                        )}
                    </div>

                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                                From Date
                            </label>
                            {datePickerVersion === '1' && (
                                <div className="relative">
                                    <Input
                                        type="date"
                                        value={localFromDate}
                                        onChange={(e) => handleFromDateChangeString(e.target.value)}
                                        className="w-full pr-8"
                                    />
                                    <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                </div>
                            )}
                            {datePickerVersion === '2' && (
                                <DatePickerV3
                                    value={localFromDate ? new Date(ensure2230Z(localFromDate) as any) : undefined}
                                    initialSelectedDate={localFromDate ? new Date(localFromDate) : undefined}
                                    onChange={handleFromDateChange}
                                    className="h-10 w-full dark:bg-gray-800 dark:border-gray-600"
                                    placeholder="mm/dd/yyyy"
                                    dateFormat="MM/dd/yyyy"
                                />
                            )}
                        </div>

                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                                To Date
                            </label>
                            {datePickerVersion === '1' && (
                                <div className="relative">
                                    <Input
                                        type="date"
                                        value={localToDate}
                                        onChange={(e) => handleToDateChangeString(e.target.value)}
                                        className="w-full pr-8"
                                    />
                                    <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                </div>
                            )}
                            {datePickerVersion === '2' && (
                                <DatePickerV3
                                    value={localToDate ? new Date(ensure2230Z(localToDate) as any) : undefined}
                                    initialSelectedDate={localToDate ? new Date(localToDate) : undefined}
                                    onChange={handleToDateChange}
                                    disableToDate={localFromDate ? new Date(new Date(localFromDate).getTime() - 86400000) : undefined}
                                    className="h-10 w-full dark:bg-gray-800 dark:border-gray-600"
                                    placeholder="mm/dd/yyyy"
                                    dateFormat="MM/dd/yyyy"
                                />
                            )}
                        </div>
                    </div>

                </div>
            </PopoverContent>
        </Popover>
    );
};

export default PopoverDateRangeFilterComponent;
